import { NamedElementSelector, NamedElementRecordMatch } from './types';
import { SelectorChain, SelectorPart } from '@fullstory/proto/fs/selectors';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import {
  SelectorMatcher,
  ToSelectorParts,
  parseSelector,
  compareSelectorDepths,
} from '@fullstory/selectors';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { namedElementsToSelectors, sortSelectorsBySpecificity } from './util';

/**
 * NamedElementMatcher matches selectors to named elements.
 * See {@link SelectorMatcher} for details on selector matching rules.
 */
export class NamedElementMatcher {
  private _selectorMatcher: SelectorMatcher = new SelectorMatcher();
  private _namedElementSelectors: NamedElementSelector[];

  public constructor(private _idsToRecord: Map<string, NamedElementRecord>) {
    this._namedElementSelectors = sortSelectorsBySpecificity(
      namedElementsToSelectors([..._idsToRecord.values()].filter(e => !e?.metadata?.archived)),
    );
  }

  /**
   * Matches the selector to named elements, returning the matched named element records.
   *
   * @param selector the selector to be matched
   * @param nativePlatform the platform to match the named element against.  A value of NATIVE_PLATFORM_INVALID (the default) will match against all platforms.
   * @param limit the max number of named elements that should be matched (defaults to unlimited - 0)
   * @param fullDepthMatch returns a single match, only if the last part of the selector matches the element's last part
   */
  public match(
    selector: SelectorPart[],
    nativePlatform: NativePlatform = NativePlatform.NATIVE_PLATFORM_INVALID,
    limit: number = 0,
    fullDepthMatch = false,
  ): NamedElementRecord[] {
    const selectorTokenSet = this.buildSelectorTokenSet(selector);

    const matched: { sel: NamedElementSelector; depths: number[] }[] = [];
    for (const namedElementSelector of this._namedElementSelectors) {
      if (
        this.validPlatform(nativePlatform, namedElementSelector.platforms) &&
        !this.fastMismatch(selectorTokenSet, namedElementSelector.parsedSelector)
      ) {
        const { matches, depths } = this._selectorMatcher.matchesWithDepths(
          namedElementSelector.orderedSelectorRules,
          selector,
          fullDepthMatch,
        );
        if (matches) {
          matched.push({ sel: namedElementSelector, depths });
        }
      }
    }

    matched.sort((a, b) => compareSelectorDepths(a.depths, b.depths));

    const retMatched: NamedElementRecord[] = [];
    const matchedIds = new Set<string>();
    for (const match of matched) {
      if (!matchedIds.has(match.sel.id)) {
        retMatched.push(this._idsToRecord.get(match.sel.id)!);
        matchedIds.add(match.sel.id);
        if (limit !== 0 && retMatched.length >= limit) {
          break;
        }
      }
    }
    return retMatched;
  }

  /**
   * Matches the selector to named elements, returning the matched named element records as well as
   * additional information about the match (ie. whether the match is full-depth).
   * Use `match` if you just need general matching.
   *
   * @param selectorToMatch the string selector to match against elements
   * @param nativePlatform the platform to match the named element against.  A value of NATIVE_PLATFORM_INVALID (the default) will match against all platforms.
   * @param limit the max number of named elements that should be matched (defaults to unlimited - 0)
   */
  public getElementMatches(
    selectorToMatch: string,
    nativePlatform: NativePlatform = NativePlatform.NATIVE_PLATFORM_INVALID,
    limit: number = 0,
  ): NamedElementRecordMatch[] {
    if (!selectorToMatch) return [];

    const matchedNamedElementRecords: NamedElementRecordMatch[] = [];

    try {
      const selectorParts = ToSelectorParts(parseSelector(selectorToMatch));
      matchedNamedElementRecords.push(
        ...this.match(selectorParts, nativePlatform, limit).map(ne => ({
          ...ne,
          fullDepthMatch: false,
        })),
      ); // arbitrarily limit to 5 named elements per event
      // the "exact" element that user is clicking on - we only want 1
      const fullDepthElement = this.match(selectorParts, nativePlatform, 1, true)[0];

      if (fullDepthElement) {
        const fullDepthElementMatch: NamedElementRecordMatch = {
          ...fullDepthElement,
          fullDepthMatch: true,
        };

        // since the full depth element is included in all initial matches, we need to replace it
        // with the one marked as full depth
        const indexOfMatch = matchedNamedElementRecords.findIndex(
          e =>
            e.namedElement &&
            fullDepthElementMatch.namedElement &&
            e.namedElement.id == fullDepthElementMatch.namedElement.id,
        );

        // we should have matched it, but just in case we didn't, push to array
        if (indexOfMatch >= 0) {
          matchedNamedElementRecords[indexOfMatch] = fullDepthElementMatch;
        } else {
          // pop one off to maintain caller's expectations of the limit
          matchedNamedElementRecords.pop();
          matchedNamedElementRecords.push(fullDepthElementMatch);
        }
      }
    } catch (e) {
      // No big deal, just don't show named element matches.
      console.error(`Error parsing ${selectorToMatch}:`, e);
    }

    return matchedNamedElementRecords;
  }

  /**
   * matchById returns the named element record for a named element that matches the specified id.
   * @param elementId the id to search for.
   */
  public matchById(elementId: string): NamedElementRecord | undefined {
    return this._idsToRecord.get(elementId);
  }

  // buildSelectorTokenSet builds a token set to use with fastMismatch to determine possible quick exclusions of selectors
  // before performing more expensive selector matching.
  private buildSelectorTokenSet(selector: SelectorPart[]): Set<string> {
    const selectorTokenSet = new Set<string>();
    for (const part of selector) {
      if (part.tag && part.tag !== '*') selectorTokenSet.add(part.tag);
      if (part.id) selectorTokenSet.add(part.id);
      if (part.classes) part.classes.forEach(clazz => selectorTokenSet.add(clazz));
      if (part.attrs) {
        for (const key in part.attrs) {
          // always add the key
          selectorTokenSet.add(key);
          // optionally add the value if exact equality
          const attrMatch = part.attrMatch[key];
          if (!attrMatch || attrMatch == '=') {
            const attrVal = part.attrs[key];
            if (attrVal) {
              selectorTokenSet.add(attrVal);
            }
          }
        }
      }
    }
    return selectorTokenSet;
  }

  // fastMismatch just does a basic set intersection ensuring that each of the required tags, ids, classes, attr keys from parsedSelector are present.
  // If any are missing, a mismatch is returned as we know the selector tokens couldn't match in any order.
  private fastMismatch(
    selectorTokenSet: Set<string>,
    parsedSelector: SelectorChain | null,
  ): boolean {
    if (!parsedSelector) return false;
    if (parsedSelector.rule) {
      const rule = parsedSelector.rule;
      if (rule.tag && rule.tag !== '*' && !selectorTokenSet.has(rule.tag)) return true;
      if (rule.id && !selectorTokenSet.has(rule.id)) return true;
      if (rule.classes && rule.classes.some(clazz => !selectorTokenSet.has(clazz))) return true;
      if (rule.attrs) {
        for (const key in rule.attrs) {
          // always check the key (unless class or id matchers, we typically always have those and don't represent the key in the set)
          if (key !== 'class' && key !== 'id' && !selectorTokenSet.has(key)) return true;
          // optionally check the value if exact equality
          const attrMatch = rule.attrMatch[key];
          if (!attrMatch || attrMatch == '=') {
            const attrVal = rule.attrs[key];
            if (attrVal && !selectorTokenSet.has(attrVal)) return true;
          }
        }
      }
    }
    return this.fastMismatch(selectorTokenSet, parsedSelector.parent);
  }
  private validPlatform(
    currentPlatform: NativePlatform,
    expectedPlatforms: NativePlatform[],
  ): boolean {
    return (
      currentPlatform === NativePlatform.NATIVE_PLATFORM_INVALID || // invalid platform means "we don't want to check platform here"
      expectedPlatforms.length === 0 || // empty list means "all platforms"
      expectedPlatforms.includes(currentPlatform) // platform actually in our list of accecpted platforms
    );
  }
}
